import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faDownload, faExternalLinkAlt, faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import jsPDF from 'jspdf';

ChartJS.register(ArcElement, Tooltip, Legend);

const GROUP_NAMES = [
    { label: "Learning & Development", emoji: "📚" },
    { label: "MSME Productivity", emoji: "⚡" },
    { label: "MSME Kaizen", emoji: "🔄" },
    { label: "Finance & Compliance", emoji: "💰" },
    { label: "People & Innovation", emoji: "👥" },
    { label: "Market & Engagement", emoji: "🛍️" },
    { label: "SAKSHAM Adoption", emoji: "🧗" },
];

const MODULES = [
  { label: "MSME Success Kit", emoji: "🧰", link: "/startupkit", group: "Learning & Development" },
    { label: "Owner's Awareness & Literacy", emoji: "📚", link: "/msmebookworld", group: "Learning & Development" },
    { label: "MSME Monthly", emoji: "🗓️", link: "/msme-monthly", group: "Learning & Development" },
    { label: "MSME Shelf", emoji: "📖", link: "/bookshelf", group: "Learning & Development" },
    { label: "MSME Shorts", emoji: "🗞️", link: "/litenshort", group: "Learning & Development" },
    { label: "MSME Trainings & Workshops", emoji: "🎓", link: "/enroll/workshop", group: "Learning & Development" },
    { label: "MSME Podcast", emoji: "️🎙️", link: "/podcast-mylist", group: "Learning & Development" },

    { label: "MSME Ranking", emoji: "🏆", link: "/udyampragatiscore", group: "MSME Kaizen" },
    { label: "Process Scorecard", emoji: "📈", link: "/msme-processscore", group: "MSME Kaizen" },
    { label: "SAKSHAM Database", emoji: "💾", link: "/msmedb", group: "MSME Kaizen" },

    { label: "NPA Solutions", emoji: "⚠️", link: "/msme-npahome", group: "Finance & Compliance" },
    { label: "Digital Card", emoji: "📘", link: "/msme-digitalcard", group: "Finance & Compliance" },
    { label: "ValueShare", emoji: "💾", link: "/orderflow-earning-estimator", group: "Finance & Compliance" },
    { label: "Funding & Loans", emoji: "💰", link: "/fundlanes", group: "Finance & Compliance" },

    { label: "MSME Year Book", emoji: "📔", link: "/msmeyb-home", group: "Productivity" },
    { label: "DeepThinking Lab", emoji: "🧩", link: "/fivewhys-analysis-home", group: "Productivity" },
    { label: "Business Chronicles", emoji: "📘", link: "/life-chronicles-home", group: "Productivity" },
    { label: "Minutes of Meeting", emoji: "📝", link: "/tasks/add", group: "Productivity" },
    { label: "Goal Board", emoji: "🎯", link: "/msme-goalboard", group: "Productivity" },
    { label: "Process & RACI Board", emoji: "📋", link: "/msme-processraci", group: "Productivity" },
    { label: "Kanban", emoji: "🗂️", link: "/msme-kanban", group: "Productivity" },
    { label: "CommuniCapture", emoji: "🟢", link: "/communicapture-home", group: "Productivity" },
    { label: "LLM Capture", emoji: "🤖", link: "/msme-llmcapture", group: "Productivity" },

    { label: "BlueHands", emoji: "🧤", link: "/bluehands-home", group: "People & Innovation" },
    { label: "Counselling", emoji: "🧠", link: "/aicounselling-about", group: "People & Innovation" },
    { label: "Digital Boutique", emoji: "🛍️", link: "/digiboutique", group: "People & Innovation" },
    { label: "Consultants", emoji: "👨‍💼", link: "/think5whys-counsellors", group: "People & Innovation" },
    { label: "Offboarded DB", emoji: "👨‍💼", link: "/offboarded-home", group: "People & Innovation" },
    { label: "Payment Dodger DB", emoji: "👨‍💼", link: "/offboarded-home", group: "People & Innovation" },
    { label: "Design Of Experiments", emoji: "📊", link: "/doe", group: "People & Innovation" },
    { label: "Innovations", emoji: "💡", link: "/agrilink-innovationhub", group: "People & Innovation" },

    { label: "MSME Marketplace", emoji: "🏪", link: "/orderflow-home", group: "Market & Engagement" },
    { label: "CSAT", emoji: "💬", link: "/csat", group: "Market & Engagement" },
    { label: "BoloGrahak", emoji: "🙋‍♀️", link: "/bolograhak", group: "Market & Engagement" },
    { label: "Forum", emoji: "🗣️", link: "/povify-home", group: "Market & Engagement" },
    { label: "TwiWrold", emoji: "🌐", link: "/twiworld", group: "Market & Engagement" },
    { label: "MSME MetaVerse", emoji: "🕶️", link: "/agrilink-metaverse", group: "Market & Engagement" },
    { label: "MSME Games", emoji: "🎮", link: "/agrilink-games", group: "Market & Engagement" },

    { label: "Ladder", emoji: "🪜", link: "/msme-think5whysmaturity", group: "SAKSHAM Adoption" },
    { label: "Stakeholders", emoji: "👨‍💼", link: "/msme-think5whysroles", group: "SAKSHAM Adoption" },
    { label: "SAKSHAM MSME Advisor", emoji: "🤝", link: "/msme-advisors", group: "SAKSHAM Adoption" },
    { label: "Empowering PMMY Enterprises", emoji: "💪", link: "/growsure", group: "SAKSHAM Adoption" },
];

const PITCH_TEXT = `SAKSHAM MSME: Bridge between horizontal AI and vertical transformation.

Think5Whys uses LLM outputs and converts them into: action plans, RACI, tracked goals, and measurable outcomes tailored for MSMEs.`;

const DONUT_DATA = {
  labels: ['Learning', 'Productivity', 'Finance', 'People', 'Market'],
  datasets: [{
    label: 'Module focus',
    data: [22, 28, 18, 16, 16],
    backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
    borderWidth: 0,
  }],
};

const DONUT_OPTIONS = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: '#9CA3AF',
        padding: 20,
        usePointStyle: true,
      },
    },
  },
};

const TAGLINES = [
  "Beyond ChatGPT — Toward Real MSME Action.",
  "AI-Assisted. Human-Driven. MSME-Focused.",
  "Where ChatGPT stops thinking, SAKSHAM starts acting.",
];

const VALUE_MATRIX = [
  { title: 'Purpose', llm: 'Answers', saksham: 'Transformation' },
  { title: 'Accountability', llm: 'None', saksham: 'Tasks & tracking' },
  { title: 'Context', llm: 'General', saksham: 'MSME + India' },
  { title: 'Outcome', llm: 'Info', saksham: 'Measurable growth' },
];

// Main component
export default function MSMESAKSHAMVsAI() {
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const filteredModules = useMemo(() => {
    if (!query && !activeGroup) return MODULES;
    return MODULES.filter((module) => {
      const searchTerm = query.toLowerCase();
      const matchesSearch = !searchTerm || 
        module.label.toLowerCase().includes(searchTerm) || 
        module.emoji.includes(searchTerm);
      const matchesGroup = !activeGroup || module.group === activeGroup;
      return matchesSearch && matchesGroup;
    });
  }, [query, activeGroup]);

  const handleOpenModule = (module) => {
    setSelectedModule(module);
  };

  const handleCloseModule = () => {
    setSelectedModule(null);
  };

  const handleCopyPitch = () => {
    navigator.clipboard.writeText(PITCH_TEXT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
    });
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ format: 'a4', unit: 'pt' });
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(20, 20, 20);
    doc.rect(0, 0, 595, 842, 'F');
    doc.setTextColor(235, 235, 235);
    doc.text('SAKSHAM MSME — Executive Snapshot', 40, 80);
    doc.setFontSize(11);
    
    // Split pitch text into lines for better PDF rendering
    const lines = doc.splitTextToSize(PITCH_TEXT, 515);
    doc.text(lines, 40, 110);
    
    doc.save('SAKSHAM-MSME-Snapshot.pdf');
  };

  const handleClearFilters = () => {
    setQuery('');
    setActiveGroup(null);
  };

  const handleVisitModule = (e, link) => {
    e.preventDefault();
    alert('This is a demo link (no backend).');
  };

  const handleCopyModuleLink = (link) => {
    navigator.clipboard.writeText(window.location.origin + link).then(() => {
      alert('Link copied (demo)');
    }).catch((err) => {
      console.error('Failed to copy link: ', err);
    });
  };

  const renderModuleCard = (module) => (
    <div key={module.label} className="bg-gray-800/40 border border-gray-700 rounded-lg p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-xl">{module.emoji}</div>
          <div className="text-sm font-medium">{module.label}</div>
        </div>
      </div>
      <div className="text-xs text-gray-400">{module.group}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-950 to-black text-gray-200 p-4">
      <div className="mx-auto">
        <header className="flex items-start justify-between gap-3 mb-4">
          <div className="flex flex-col items-center justify-center">
            <h1 className="text-lg sm:text-xl font-semibold">SAKSHAM MSME — Compare vs AI</h1>
            <p className="text-xs text-center text-gray-200 mt-1">Why SAKSHAM is not "just another LLM" — and how it operationalizes AI suggestions.</p>
          </div>
        </header>

        {/* Search + Chart Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 place-items-center">
          <div className="sm:col-span-2 bg-gray-900/60 border border-gray-800 rounded-lg p-3">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 place-items-center">
                {GROUP_NAMES.map((group) => (
                    <button
                    key={group}
                    onClick={() => setActiveGroup(activeGroup === group.label ? null : group.label)}
                    className={`text-xs px-3 py-1 rounded-m border transition ${
                        activeGroup === group.label
                        ? 'bg-indigo-600 border-indigo-500'
                        : 'bg-teal-600 border-teal-700 hover:bg-teal-800'
                    }`}
                    >
                    {group.emoji}{group.label}
                    </button>
                ))}
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filteredModules.map(renderModuleCard)}
            </div>
          </div>

          <aside className="bg-gray-900/60 border border-gray-800 rounded-lg p-3">
            <div className="text-xs text-gray-400 mb-2">Module distribution</div>
            <div className="w-full h-48">
              <Doughnut data={DONUT_DATA} options={DONUT_OPTIONS} />
            </div>
            <div className="mt-2 flex flex-col items-center gap-2">
              <button 
                onClick={() => setShowPitchModal(true)} 
                className="px-2 py-2 rounded bg-indigo-600 text-sm hover:bg-indigo-700 transition"
              >
                Open
              </button>
            </div>
          </aside>
        </div>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <FeatureCard title="What LLMs (ChatGPT/Grok) Do" color="bg-gray-800/40">
            <ul className="text-sm text-gray-300 space-y-1 list-disc ml-4">
              <li>General purpose Q&amp;A and brainstorming.</li>
              <li>Quick drafts, code snippets, and ideas.</li>
              <li>Limited domain memory and no built-in accountability.</li>
            </ul>
          </FeatureCard>

          <FeatureCard title="What SAKSHAM MSME Does" color="bg-indigo-900/30">
            <ul className="text-sm text-gray-300 space-y-1 list-disc ml-4">
              <li>Domain-aware workflows mapped to MSME needs (finance, compliance, markets).</li>
              <li>Execution tools: Goal Board, Kanban, RACI, Minutes, and Process boards.</li>
              <li>Think5Whys — converts LLM outputs into root-cause action plans and assignments.</li>
            </ul>
          </FeatureCard>
        </section>

        <section className="bg-gray-900/60 border border-gray-800 rounded-lg p-3 mb-6">
          <h2 className="text-sm font-semibold mb-2">Value Matrix</h2>
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-300">
            {VALUE_MATRIX.map((item) => (
              <div key={item.title} className="p-2 bg-gray-800/30 rounded">
                {item.title}
                <br />
                <span className="text-gray-400">
                  LLMs: {item.llm} • SAKSHAM: {item.saksham}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gray-900/50 border border-gray-800 rounded-lg p-3 mb-8">
          <h4 className="text-sm font-semibold">SAKSHAM MSME by Think5Whys</h4>
          <div className="mt-2 flex flex-col gap-2 text-xs text-gray-300">
            {TAGLINES.map((tagline, index) => (
              <div key={index} className="p-2 bg-gray-800/30 rounded">{tagline}</div>
            ))}
          </div>
        </section>

        <footer className="text-xs text-gray-500 mb-8 flex items-center justify-between">
          <div>Made with ❤️ for MSMEs</div>
          <div className="flex items-center gap-3">
            <a className="underline text-gray-400 text-[11px] hover:text-gray-300" href="#" aria-label="Documentation">
              Docs
            </a>
            <a className="flex items-center gap-1 text-gray-400 text-[11px] hover:text-gray-300" href="#" aria-label="GitHub Repository">
              <FontAwesomeIcon icon={faGithub} /> Repo
            </a>
          </div>
        </footer>
      </div>

      {selectedModule && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="w-full sm:w-3/4 max-w-2xl bg-gray-900 rounded-lg border border-gray-800 p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-2xl">{selectedModule.emoji} {selectedModule.label}</div>
                <div className="text-xs text-gray-400">{selectedModule.group}</div>
              </div>
              <button 
                onClick={handleCloseModule} 
                className="p-2 bg-gray-800 rounded hover:bg-gray-700 transition"
                aria-label="Close module"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="mt-3 text-sm text-gray-300">
              <p>In the real SAKSHAM product this module would contain:</p>
              <ul className="list-disc ml-5 mt-2 text-gray-400">
                <li>Detailed workflows & checklists</li>
                <li>Integration with Think5Whys analysis</li>
                <li>Assign-to-user, RACI and Goal tracking</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showPitchModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="w-full max-w-2xl bg-gradient-to-b from-gray-900 to-gray-800 border border-gray-700 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">SAKSHAM MSME — Executive Pitch</h3>
              <button 
                onClick={() => setShowPitchModal(false)} 
                className="p-2 w-6 bg-gray-800 rounded hover:bg-gray-700 transition"
                aria-label="Close pitch modal"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="mt-3 text-sm text-gray-300 space-y-3">
              <p><strong>One-line:</strong> SAKSHAM MSME is the bridge between AI suggestions and real MSME transformation — an execution-first, domain-aware platform.</p>
              <p><strong>Problem:</strong> Horizontal LLMs provide answers but not accountability, nor domain-specific execution for MSMEs.</p>
              <p><strong>Solution:</strong> Think5Whys + SAKSHAM modules convert LLM output into RACI, tracked tasks, and measurable outcomes.</p>
              <p><strong>Key modules:</strong> Goal Board, Kanban, RACI Board, LLM Capture, DeepThinking Lab, Finance & Compliance tools, Marketplace.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeatureCard({ title, color = 'bg-gray-800/30', children }) {
  return (
    <div className={`${color} border border-gray-800 rounded-lg p-3`}> 
      <h4 className="text-sm font-semibold mb-2">{title}</h4>
      <div>{children}</div>
    </div>
  );
}